import React from 'react';
import Icon from '../../../components/AppIcon';
import Image from '../../../components/AppImage';
import Button from '../../../components/ui/Button';

const RecipientDetailsModal = ({ isOpen, onClose, recipient, onEdit, onSendMoney }) => {
  if (!isOpen || !recipient) return null;

  const getVerificationStatusColor = (status) => {
    switch (status) {
      case 'verified':
        return 'text-success bg-success/10 border-success/20';
      case 'pending':
        return 'text-warning bg-warning/10 border-warning/20';
      case 'failed':
        return 'text-error bg-error/10 border-error/20';
      default:
        return 'text-muted-foreground bg-muted border-border';
    }
  };

  const getVerificationIcon = (status) => {
    switch (status) {
      case 'verified':
        return 'CheckCircle';
      case 'pending':
        return 'Clock';
      case 'failed':
        return 'XCircle';
      default:
        return 'AlertCircle';
    }
  };

  const formatLastUsed = (date) => {
    const lastUsed = new Date(date);
    return lastUsed?.toLocaleDateString('en-GB', {
      day: '2-digit',
      month: 'short',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  // Mock CoP verification details
  const copDetails = {
    verified: {
      status: 'Match',
      message: 'Account holder name matches bank records',
      checkedAt: '2024-11-15T10:30:00Z',
      confidence: 'High'
    },
    pending: {
      status: 'In Progress',
      message: 'Verification request submitted to bank',
      checkedAt: '2024-11-18T09:15:00Z',
      confidence: 'Pending'
    },
    failed: {
      status: 'No Match',
      message: 'Account holder name does not match bank records',
      checkedAt: '2024-11-17T14:20:00Z',
      confidence: 'Low'
    }
  };

  const copInfo = copDetails?.[recipient?.verificationStatus] || copDetails?.pending;

  return (
    <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
      <div className="bg-card border border-border rounded-lg w-full max-w-2xl max-h-[90vh] overflow-y-auto">
        {/* Header */}
        <div className="flex items-center justify-between p-6 border-b border-border">
          <div className="flex items-center space-x-3">
            <div className="w-12 h-12 bg-primary/10 rounded-full flex items-center justify-center">
              <Icon name="User" size={20} className="text-primary" />
            </div>
            <div>
              <h2 className="text-xl font-semibold text-foreground">{recipient?.name}</h2>
              <p className="text-sm text-muted-foreground capitalize">{recipient?.relationship}</p>
            </div>
          </div>
          <Button
            variant="ghost"
            size="sm"
            onClick={onClose}
            iconName="X"
            iconSize={20}
          />
        </div>

        {/* Content */}
        <div className="p-6 space-y-6">
          {/* Verification Status */}
          <div>
            <h3 className="text-lg font-medium text-foreground mb-4">Verification Status</h3>
            <div className="bg-muted/30 rounded-lg p-4">
              <div className="flex items-center justify-between mb-3">
                <div className={`flex items-center space-x-2 px-3 py-1 rounded-full text-sm font-medium border ${getVerificationStatusColor(recipient?.verificationStatus)}`}>
                  <Icon name={getVerificationIcon(recipient?.verificationStatus)} size={16} />
                  <span className="capitalize">{recipient?.verificationStatus}</span>
                </div>
                <span className="text-sm text-muted-foreground">
                  Checked: {formatLastUsed(copInfo?.checkedAt)}
                </span>
              </div>
              
              <div className="space-y-2">
                <div className="flex justify-between text-sm">
                  <span className="text-muted-foreground">CoP Status:</span>
                  <span className="text-foreground font-medium">{copInfo?.status}</span>
                </div>
                <div className="flex justify-between text-sm">
                  <span className="text-muted-foreground">Confidence:</span>
                  <span className="text-foreground">{copInfo?.confidence}</span>
                </div>
                <p className="text-sm text-muted-foreground mt-2">{copInfo?.message}</p>
              </div>
            </div>
          </div>

          {/* Banking Details */}
          <div>
            <h3 className="text-lg font-medium text-foreground mb-4">Banking Details</h3>
            <div className="bg-muted/30 rounded-lg p-4 space-y-4">
              <div className="flex items-center space-x-3">
                <Icon name="Building" size={20} className="text-muted-foreground" />
                <div>
                  <p className="text-sm font-medium text-foreground">{recipient?.bankName}</p>
                  <p className="text-xs text-muted-foreground">Primary Bank</p>
                </div>
              </div>
              
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div>
                  <p className="text-xs text-muted-foreground mb-1">IBAN</p>
                  <p className="text-sm font-mono text-foreground bg-background px-3 py-2 rounded border">
                    {recipient?.iban}
                  </p>
                </div>
                <div>
                  <p className="text-xs text-muted-foreground mb-1">SWIFT/BIC</p>
                  <p className="text-sm font-mono text-foreground bg-background px-3 py-2 rounded border">
                    {recipient?.swiftCode}
                  </p>
                </div>
              </div>
              
              <div className="flex items-center justify-between pt-2 border-t border-border">
                <div className="flex items-center space-x-2">
                  <Image
                    src={recipient?.countryFlag}
                    alt={`${recipient?.country} flag`}
                    className="w-6 h-4 rounded-sm object-cover"
                  />
                  <span className="text-sm font-medium text-foreground">{recipient?.country}</span>
                </div>
                
                <div className="flex space-x-2">
                  {recipient?.supportedCurrencies?.map((currency) => (
                    <span
                      key={currency}
                      className="px-2 py-1 text-xs font-medium bg-primary/10 text-primary rounded"
                    >
                      {currency}
                    </span>
                  ))}
                </div>
              </div>
            </div>
          </div>

          {/* Usage Information */}
          <div>
            <h3 className="text-lg font-medium text-foreground mb-4">Usage Information</h3>
            <div className="bg-muted/30 rounded-lg p-4 space-y-3">
              <div className="flex justify-between text-sm">
                <span className="text-muted-foreground">Last Used:</span>
                <span className="text-foreground">{formatLastUsed(recipient?.lastUsed)}</span>
              </div>
              <div className="flex justify-between text-sm">
                <span className="text-muted-foreground">Total Transfers:</span>
                <span className="text-foreground">12 transactions</span>
              </div>
              <div className="flex justify-between text-sm">
                <span className="text-muted-foreground">Total Amount:</span>
                <span className="text-foreground">€15,750.00</span>
              </div>
              <div className="flex justify-between text-sm">
                <span className="text-muted-foreground">Added:</span>
                <span className="text-foreground">15 Oct 2024</span>
              </div>
            </div>
          </div>
        </div>

        {/* Actions */}
        <div className="flex justify-between p-6 border-t border-border">
          <Button
            variant="outline"
            onClick={() => onEdit(recipient)}
            iconName="Edit"
            iconPosition="left"
            iconSize={16}
          >
            Edit Details
          </Button>
          
          <Button
            variant="default"
            onClick={() => {
              onSendMoney(recipient);
              onClose();
            }}
            iconName="Send"
            iconPosition="left"
            iconSize={16}
          >
            Send Money
          </Button>
        </div>
      </div>
    </div>
  );
};

export default RecipientDetailsModal;